import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  CreditCard, ArrowUp, ArrowDown, Search, Filter,
  DollarSign, Clock, Wallet, ArrowRight, ChevronDown,
  BarChart4, Calendar, Download, Zap, AlertTriangle,
  Sun, Moon, Globe, MapPin, Settings, Plus, X, Trash,
  Loader2, Building2
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/badge";
import SharedFooter3 from "../Footer/SharedFooter3";
import SharedHeader4 from "../Headers/SharedHeader4";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  fetchSubscriptionPlansByType,
  clearSubscriptionPlansError
} from "../redux/Slices/subscriptionPlanSlice";
import {
  fetchUserCredits,
  fetchUserCreditBalance,
  clearUserCreditsError,
  clearUserBalanceError
} from "../redux/Slices/creditSlice";
import { getAllCountries, getCountryById } from "../redux/Actions/AdminAction";
import { getUserCategories } from "../redux/Actions/AdminAction";
import { getAllCurrencies } from "../redux/Actions/AuthAction";
import { getCurrencies, getCurrencyByCodeAction } from "../redux/Actions/CurrencyActions";
import { CreditStatusEnum, SubscriptionPlanTypeEnum, SubscriptionPlanAvailableTimeEnum, RoleEnum } from "../modules";
import { PaymentMethodsProvider } from "../contexts/PaymentMethodsContext";
import CreditPaymentModal from "../components/payment/CreditPaymentModal";
import { getCachedCountryPreference, setCachedCountryPreference, setCachedCurrencyPreference, getDynamicCurrencySymbol, getCurrencySymbol } from "../lib/currencyUtils";
import { convertCurrency, getCurrencyByCode } from "../lib/currencyConversion";
import { convertCurrencyAmount } from "../redux/Actions/CurrencyActions";
import ProfileCompletionDialog from "../components/ui/ProfileCompletionDialog";
import VerificationRequirementDialog from "../components/VerificationRequirementDialog";
import { useVerification } from "../hooks/useVerification";
import { useNavigate } from "react-router-dom";
import { checkUserBillingInfo } from "../utils/billingUtils";
import BillingAddressRequirementDialog from "../components/ui/BillingAddressRequirementDialog";
import CountryDropdown from "../components/ui/CountryDropdown";

const EURO_CURRENCY_COUNTRIES = new Set([
  "AD", "AT", "AX", "BE", "CY", "DE", "EE", "ES", "FI", "FR",
  "GF", "GP", "GR", "IE", "IT", "LT", "LU", "LV", "MC", "ME",
  "MF", "MQ", "MT", "NC", "NL", "PM", "PT", "RE", "SI", "SK",
  "SM", "TF", "VA", "WF", "YT"
]);

// Component for displaying a transaction row
const TransactionRow = ({ transaction }) => {
  const { t } = useTranslation();
  const isCredit = transaction.type === "credit";
  const isEmergency = transaction.isEmergency;
  const isSubscription = transaction.isSubscription;
  const isUsed = Number(transaction.credit?.status.id) === CreditStatusEnum.Used || Number(transaction.credit?.status.id) === CreditStatusEnum.Used_Emergency;
  return (
    <div className="py-4 hover:bg-gray-50 px-4 md:px-6 rounded-lg transition-colors border-b border-gray-100 last:border-b-0" style={{ textAlign: "left" }}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className={`p-2 rounded-lg flex-shrink-0 ${isSubscription ? "bg-purple-100" :
          isCredit ? (isEmergency ? "bg-red-100" : "bg-green-100") : "bg-blue-100"
          }`}>
          {isSubscription ? (
            <Clock className="h-5 w-5 text-purple-600" />
          ) : isUsed ? (
            <ArrowUp className={`h-5 w-5 ${isUsed ? "text-red-600" : "text-green-600"}`} />
          ) : isCredit ? (
            <ArrowDown className={`h-5 w-5 ${isEmergency ? "text-red-600" : "text-green-600"}`} />
          ) : (
            <ArrowDown className="h-5 w-5 text-blue-600" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <p className="font-medium text-gray-900 truncate">{transaction.description}</p>
            <div className="flex flex-wrap gap-1">
              {isEmergency && !isSubscription && (
                <Badge variant="destructive" className="text-xs whitespace-nowrap">{t("creditsPage.emergency")}</Badge>
              )}
              {isSubscription && (
                <Badge variant={undefined} className="bg-purple-100 text-purple-700 text-xs whitespace-nowrap">{t("creditsPage.subscription")}</Badge>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-1">{transaction.date}</p>
          {transaction.period && (
            <p className="text-xs text-gray-500">{transaction.period}</p>
          )}
        </div>

        <div className="text-right mt-2 sm:mt-0 flex-shrink-0">
          <p className={`font-medium ${isSubscription ? "text-purple-600" :
            isCredit ? (isUsed ? "text-red-600" : "text-green-600") : "text-blue-600"
            }`}>
            {isUsed ? "-" : "+"}{transaction.amount} {t("creditsPage.credits")}
          </p>
          {transaction.discount && (
            <p className="text-xs text-green-600">{t("creditsPage.save", { percent: transaction.discount })}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Credit package component for regular credits
const CreditPackage = ({ plan, onSelect, currencySymbol, selectedCountry, currencies, selectedCurrencyCode, convertedAmounts }) => {
  const { t } = useTranslation();
  const isRegular = Number(plan.type?.id) === 1; // Regular = 1, Emergency = 2
  const badgeColor = isRegular ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700";
  const buttonColor = isRegular ? "bg-blue-600 hover:bg-blue-700" : "bg-red-600 hover:bg-red-700";

  // Find the area for the selected country (only if country is selected)
  const selectedArea = selectedCountry ? plan.areas?.find(area => area.country?.id === selectedCountry.id) : null;
  const isFallbackPricing = Boolean(selectedCountry && !selectedArea);

  // Track area amount separately from base amount
  const baseAmount = plan.amount;
  const areaAmount = selectedCountry && selectedArea ? selectedArea.amount : null;
  const displayCredits = selectedCountry && selectedArea ? selectedArea.credits : plan.credits;
  const displayDiscount = selectedCountry && selectedArea ? selectedArea.discount : plan.discount;

  // Use converted amount from API if available
  let displayAmount = baseAmount;
  let showOriginal = false;
  let effectiveCurrencySymbol = currencySymbol;

  if (isFallbackPricing) {
    displayAmount = baseAmount;
    effectiveCurrencySymbol = getCurrencySymbol("EUR");
    showOriginal = false;
  } else if (selectedCurrencyCode === "EUR") {
    displayAmount = areaAmount ?? baseAmount;
  } else if (convertedAmounts && convertedAmounts[plan.id]) {
    displayAmount = convertedAmounts[plan.id];
    showOriginal = selectedCurrencyCode !== "EUR";
  }

  return (
    <Card className="overflow-hidden h-full flex flex-col transition-all duration-300 hover:shadow-lg" style={{ textAlign: "left" }}>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex justify-between items-start mb-4">
          <div className={`p-2 rounded-lg ${isRegular ? "bg-blue-100" : "bg-red-100"}`}>
            <Wallet className={`h-5 w-5 ${isRegular ? "text-blue-600" : "text-red-600"}`} />
          </div>
          <Badge variant={undefined} className={`${badgeColor} font-medium`}>
            {plan.type?.name || "Regular"}
          </Badge>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
        <p className="text-sm text-gray-600 mb-3">{displayCredits} Credits</p>

        <div className="mt-auto pt-3">
          <div className="flex flex-col">
            <div className="flex items-baseline">
              <span className="text-2xl font-bold text-gray-900">{effectiveCurrencySymbol}{displayAmount.toFixed(2)}</span>
              {displayDiscount > 0 && (
                <span className="ml-2 text-sm text-green-600 font-medium">Save {displayDiscount}%</span>
              )}
            </div>
            {showOriginal && (
              <span className="text-xs text-gray-500 mt-1">Original: €{baseAmount.toFixed(2)} EUR</span>
            )}
          </div>
          {selectedCountry ? (
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-1">
                {selectedArea ? t("creditsPage.forCountry", { country: selectedCountry.name }) : t("creditsPage.defaultPlanCountry", { country: selectedCountry.name })}
              </p>
              <div className="flex flex-wrap gap-1">
                <Badge variant="outline" className="text-xs">
                  {selectedCountry.name}
                </Badge>
                {selectedArea ? (
                  <Badge variant="outline" className="text-xs">
                    {selectedArea.category?.name || t("creditsPage.areaSpecific")}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">
                    {t("creditsPage.basePlanPricing")}
                  </Badge>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-1">{t("creditsPage.basePlan")}</p>
              <div className="flex flex-wrap gap-1">
                <Badge variant="outline" className="text-xs">
                  {t("creditsPage.allCountries")}
                </Badge>
                {plan.areas && plan.areas.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {t("creditsPage.areas", { count: plan.areas.length })}
                  </Badge>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Button
        className={`w-full rounded-t-none py-3 ${buttonColor} text-white`}
        onClick={() => onSelect({
          ...plan,
          convertedAmount: isFallbackPricing ? undefined : displayAmount,
          baseAmount: baseAmount,
          selectedCurrencyCode: isFallbackPricing ? "EUR" : selectedCurrencyCode || "EUR",
          selectedCurrencyId: selectedCountry?.currency?.id,
          currencySymbol: effectiveCurrencySymbol,
          isFallbackPricing,
          selectedArea: selectedArea
        })}
      >
        {isRegular ? t("creditsPage.buyCredits") : t("creditsPage.subscribe")}
      </Button>
    </Card>
  );
};

// Emergency plan package component for emergency subscription plans
const EmergencyPlanPackage = ({ plan, onSelect, currencySymbol, selectedCountry, currencies, selectedCurrencyCode, convertedAmounts }) => {
  const { t } = useTranslation();
  const isEmergency = Number(plan.type?.id) === 2; // Emergency = 2
  const badgeColor = "bg-red-100 text-red-700";
  const buttonColor = "bg-red-600 hover:bg-red-700";

  // Find the area for the selected country (only if country is selected)
  const selectedArea = selectedCountry ? plan.areas?.find(area => area.country?.id === selectedCountry.id) : null;
  const isFallbackPricing = Boolean(selectedCountry && !selectedArea);

  const baseAmount = plan.amount;
  const areaAmount = selectedCountry && selectedArea ? selectedArea.amount : null;
  const displayCredits = selectedCountry && selectedArea ? selectedArea.credits : plan.credits;
  const displayDiscount = selectedCountry && selectedArea ? selectedArea.discount : plan.discount;

  // Use converted amount from API if available
  let displayAmount = baseAmount;
  let showOriginal = false;
  let effectiveCurrencySymbol = currencySymbol;

  if (isFallbackPricing) {
    displayAmount = baseAmount;
    effectiveCurrencySymbol = getCurrencySymbol("EUR");
    showOriginal = false;
  } else if (selectedCurrencyCode === "EUR") {
    displayAmount = areaAmount ?? baseAmount;
  } else if (convertedAmounts && convertedAmounts[plan.id]) {
    displayAmount = convertedAmounts[plan.id];
    showOriginal = selectedCurrencyCode !== "EUR";
  }

  // Get available time display from plan data
  const getAvailableTimeDisplay = () => {
    if (plan.availableTime?.id) {
      switch (Number(plan.availableTime.id)) {
        case SubscriptionPlanAvailableTimeEnum.Day:
          return { label: t("creditsPage.dayTime"), icon: Sun };
        case SubscriptionPlanAvailableTimeEnum.Night:
          return { label: t("creditsPage.nightTime"), icon: Moon };
        case SubscriptionPlanAvailableTimeEnum.FullDay:
          return { label: t("creditsPage.fullDay"), icon: Clock };
        default:
          return { label: t("creditsPage.notSpecified"), icon: Clock };
      }
    }
    return { label: t("creditsPage.notSpecified"), icon: Clock };
  };

  const availableTimeInfo = getAvailableTimeDisplay();
  const IconComponent = availableTimeInfo.icon;

  return (
    <Card className="overflow-hidden h-full flex flex-col transition-all duration-300 hover:shadow-lg" style={{ textAlign: "left" }}>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex justify-between items-start mb-4">
          <div className="p-2 rounded-lg bg-red-100">
            <Clock className="h-5 w-5 text-red-600" />
          </div>
          <Badge variant={undefined} className={`${badgeColor} font-medium`}>
            {plan.type?.name || "Emergency"}
          </Badge>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
        <p className="text-sm text-gray-600 mb-3">{displayCredits} Credits</p>

        {/* Available Time Display */}
        <div className="mb-4">
          <div className="flex items-center gap-2 p-3 bg-gray-50 border rounded-lg">
            <IconComponent className="h-4 w-4 text-gray-600" />
            <div>
              <p className="text-xs text-gray-500 mb-1">{t("creditsPage.availableTime")}</p>
              <p className="text-sm font-medium text-gray-900">{availableTimeInfo.label}</p>
            </div>
          </div>
        </div>

        <div className="mt-auto pt-3">
          <div className="flex flex-col">
            <div className="flex items-baseline">
              <span className="text-2xl font-bold text-gray-900">{effectiveCurrencySymbol}{displayAmount.toFixed(2)}</span>
              {displayDiscount > 0 && (
                <span className="ml-2 text-sm text-green-600 font-medium">{t("creditsPage.save", { percent: displayDiscount })}</span>
              )}
            </div>
            {showOriginal && (
              <span className="text-xs text-gray-500 mt-1">Original: €{baseAmount.toFixed(2)} EUR</span>
            )}
          </div>
          {selectedCountry ? (
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-1">
                {selectedArea ? t("creditsPage.forCountry", { country: selectedCountry.name }) : t("creditsPage.defaultPlanCountry", { country: selectedCountry.name })}
              </p>
              <div className="flex flex-wrap gap-1">
                <Badge variant="outline" className="text-xs">
                  {selectedCountry.name}
                </Badge>
                {selectedArea ? (
                  <Badge variant="outline" className="text-xs">
                    {selectedArea.category?.name || t("creditsPage.areaSpecific")}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">
                    {t("creditsPage.basePlanPricing")}
                  </Badge>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-1">{t("creditsPage.basePlan")}</p>
              <div className="flex flex-wrap gap-1">
                <Badge variant="outline" className="text-xs">
                  {t("creditsPage.allCountries")}
                </Badge>
                {plan.areas && plan.areas.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {t("creditsPage.areas", { count: plan.areas.length })}
                  </Badge>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Button
        className={`w-full rounded-t-none py-3 ${buttonColor} text-white`}
        onClick={() => onSelect({
          ...plan,
          convertedAmount: isFallbackPricing ? undefined : displayAmount,
          baseAmount: baseAmount,
          selectedCurrencyCode: isFallbackPricing ? "EUR" : selectedCurrencyCode || "EUR",
          selectedCurrencyId: selectedCountry?.currency?.id,
          currencySymbol: effectiveCurrencySymbol,
          isFallbackPricing,
          selectedArea: selectedArea
        })}
      >
        {t("creditsPage.subscribeToEmergencyPlan")}
      </Button>
    </Card>
  );
};

// Subscription package component
const SubscriptionPackage = ({ selectedOptions, currencySymbol }) => {
  const { t } = useTranslation();
  const { timeSlot, provinces, categories } = selectedOptions;

  const basePrice = timeSlot === "day" ? 300 : timeSlot === "night" ? 250 : 500;
  const provincePrice = (provinces.length - 1) * 50;
  const categoryPrice = (categories.length - 1) * 75;
  const totalPrice = basePrice + provincePrice + categoryPrice;
  const discount = totalPrice * 0.1;
  const finalPrice = totalPrice - discount;

  const timeSlotLabel =
    timeSlot === "day" ? "6am - 6pm" :
      timeSlot === "night" ? "6pm - 6am" :
        "All Day (24 hours)";

  return (
    <Card className="p-6 border-purple-200 shadow-md h-full flex flex-col" style={{ textAlign: "left" }}>
      <div className="flex justify-between items-start mb-4">
        <div className="p-2 rounded-lg bg-purple-100">
          <Clock className="h-5 w-5 text-purple-600" />
        </div>
        <Badge variant={undefined} className="bg-purple-100 text-purple-700 font-medium">
          {t("creditsPage.emergencySubscriptionTitle")}
        </Badge>
      </div>

      <h3 className="text-xl font-bold text-gray-900 mb-4">{t("creditsPage.subscriptionSummary")}</h3>

      <div className="space-y-3 mb-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" />
            <span className="text-gray-700">{t("creditsPage.timeSlot")}</span>
          </div>
          <span className="font-medium">{timeSlotLabel}</span>
        </div>

        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-gray-500" />
            <span className="text-gray-700">{t("creditsPage.countries")}</span>
          </div>
          <span className="font-medium text-right max-w-[60%]">{provinces.join(", ")}</span>
        </div>

        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-gray-500" />
            <span className="text-gray-700">{t("creditsPage.categories")}</span>
          </div>
          <span className="font-medium text-right max-w-[60%]">{categories.join(", ")}</span>
        </div>
      </div>

      <div className="space-y-2 mb-6 border-t border-b py-4">
        <div className="flex justify-between">
          <span className="text-gray-700">{t("creditsPage.basePrice")}</span>
          <span className="font-medium">{currencySymbol}{basePrice}</span>
        </div>

        {provincePrice > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-700">{t("creditsPage.additionalCountries")}</span>
            <span className="font-medium">{currencySymbol}{provincePrice}</span>
          </div>
        )}

        {categoryPrice > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-700">{t("creditsPage.additionalCategories")}</span>
            <span className="font-medium">{currencySymbol}{categoryPrice}</span>
          </div>
        )}

        <div className="flex justify-between">
          <span className="text-gray-700">{t("creditsPage.discount")}</span>
          <span className="font-medium text-green-600">-{currencySymbol}{discount}</span>
        </div>

        <div className="flex justify-between pt-2 border-t">
          <span className="font-bold text-gray-900">{t("creditsPage.total")}</span>
          <span className="font-bold text-lg text-purple-600">{currencySymbol}{finalPrice}</span>
        </div>
      </div>

      <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white mt-auto">
        {t("creditsPage.purchaseSubscription")}
      </Button>
    </Card>
  );
};

// Main Credits Page Component
const CreditsPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Redux state
  const { plans, plansLoading, plansError } = useAppSelector(
    (state) => state.subscriptionPlans
  );
  const { countries: allCountries } = useAppSelector((state) => state.admin);
  const { categories } = useAppSelector((state) => state.admin);
  const { user } = useAppSelector((state) => state.auth);
  const {
    userCredits,
    userCreditsLoading,
    userCreditsError,
    userBalance,
    userBalanceLoading,
    userBalanceError
  } = useAppSelector((state) => state.credits);

  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [creditType, setCreditType] = useState(() => {
    // Load saved tab from localStorage on initial render
    const savedTab = localStorage.getItem("credits_selected_tab");
    return savedTab === "emergency" ? "emergency" : "regular";
  });
  const [searchQuery, setSearchQuery] = useState("");

  // Country selection
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isDetectingCountry, setIsDetectingCountry] = useState(false);
  const [convertedAmounts, setConvertedAmounts] = useState<{[key: string]: number}>({});
  const [isConvertingAmounts, setIsConvertingAmounts] = useState(false);

  // Get currencies from Redux
  const { currencies } = useAppSelector((state: any) => state.auth);

  // Get selected currency code based on selected country
  const getSelectedCurrencyCode = () => {
    if (selectedCountry && selectedCountry.currency) {
      return selectedCountry.currency.initial || selectedCountry.currency.code || "EUR";
    }
    return "EUR"; // Default to EUR (base currency)
  };

  const selectedCurrencyCode = getSelectedCurrencyCode();

  // Get currency symbol based on selected currency code
  const getCurrencySymbolForDisplay = () => {
    if (selectedCountry && selectedCountry.currency) {
      if (selectedCountry.currency.symbol) {
        return selectedCountry.currency.symbol;
      }
      const codeForSymbol = selectedCountry.currency.initial || selectedCountry.currency.code;
      if (codeForSymbol) {
        return getCurrencySymbol(codeForSymbol);
      }
    }
    return "€"; // EUR symbol as default
  };

  // Get currency ID from currency code
  const getCurrencyIdByCode = (code: string) => {
    if (!currencies || currencies.length === 0) return null;
    const currency = currencies.find((c: any) => c.initial === code || c.code === code);
    return currency?.value || null;
  };

  // CreditPaymentModal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Profile completion dialog state
  const [showProfileCompletionDialog, setShowProfileCompletionDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [showBillingAddressDialog, setShowBillingAddressDialog] = useState(false);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null);
  
  // Use verification hook
  const { isVerified, goToVerification, verificationData } = useVerification();
  
  // Track if verification data has been loaded at least once
  const [verificationDataLoaded, setVerificationDataLoaded] = useState(false);

  // Emergency subscription options - will be updated when data loads
  const [subscriptionOptions, setSubscriptionOptions] = useState({
    timeSlot: "day",
    provinces: [],
    categories: []
  });

  // Use countries from Redux or fallback to hardcoded
  const availableProvinces = allCountries?.length > 0
    ? allCountries.map(country => country.name)
    : ["Toronto", "Montreal", "Vancouver", "Calgary", "Ottawa"];

  const availableCategories = categories?.data?.length > 0
    ? categories.data.map(category => category.name)
    : ["Plumber", "Electrician", "Carpenter", "Painter", "HVAC Technician"];

  // Filter plans by selected country (plans are already filtered by type from API)
  const filteredPlans = plans.filter(plan => {
    // If no country selected, show all plans
    if (!selectedCountry) return true;

    // Filter by selected country - check if plan has areas for this country
    return plan.areas?.some(area => area.country?.id === selectedCountry.id);
  });

  // If no plans match the selected country, show default plans (plans without country-specific areas)
  const displayPlans = filteredPlans.length > 0 ? filteredPlans : plans;

  // Load data on component mount
  useEffect(() => {
    dispatch(getAllCountries({ page: 1, limit: 10, isShowLoader: false }));
    dispatch(getUserCategories({ page: 1, limit: 10, isShowLoader: false }));
    dispatch(clearSubscriptionPlansError());
    dispatch(clearUserCreditsError());
    dispatch(clearUserBalanceError());
  }, [dispatch]);

  // Ensure currencies are loaded
  useEffect(() => {
    if (!currencies || currencies.length === 0) {
      dispatch(getAllCurrencies({ isShowLoader: false }));
    }
  }, [dispatch, currencies]);

  // Country detection is now handled by CountryDropdown component with autoDetect prop
  // When CountryDropdown auto-detects, it will trigger onCountrySelect callback
  // which will set selectedCountry and trigger conversion

  // Fetch user credits and balance when user is available
  useEffect(() => {
    if (user?.id) {
      dispatch(fetchUserCredits({ userId: user.id }));
      dispatch(fetchUserCreditBalance(user.id));
    }
  }, [dispatch, user?.id]);

  // Save creditType to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("credits_selected_tab", creditType);
  }, [creditType]);

  // Load subscription plans based on selected tab
  useEffect(() => {
    if (creditType === "regular") {
      dispatch(fetchSubscriptionPlansByType(SubscriptionPlanTypeEnum.Regular));
    } else if (creditType === "emergency") {
      dispatch(fetchSubscriptionPlansByType(SubscriptionPlanTypeEnum.Emergency));
    }
  }, [dispatch, creditType]);

  // Convert amounts using API when country or plans change
  useEffect(() => {
    const convertPlanAmounts = async () => {
      if (!selectedCountry || !selectedCountry.currency || !currencies || currencies.length === 0 || !plans || plans.length === 0) {
        setConvertedAmounts({});
        return;
      }

      const targetCurrencyCode = selectedCountry.currency.initial || selectedCountry.currency.code;
      
      // If target currency is EUR (base currency), no conversion needed
      if (targetCurrencyCode === "EUR") {
        setConvertedAmounts({});
        return;
      }

      setIsConvertingAmounts(true);
      
      try {
        const convertedResults: {[key: string]: number} = {};

        // Convert each plan's amount using the API
        for (const plan of plans) {
          const selectedArea = selectedCountry
            ? plan.areas?.find((area: any) => area.country?.id === selectedCountry.id)
            : null;

          const baseAmountInput =
            typeof plan.amount === "number"
              ? plan.amount
              : plan.amount
              ? Number(plan.amount)
              : undefined;

          const fallbackAreaAmount =
            selectedArea && typeof selectedArea.amount === "number"
              ? selectedArea.amount
              : selectedArea?.amount
              ? Number(selectedArea.amount)
              : undefined;

          const baseAmount = baseAmountInput ?? fallbackAreaAmount ?? 0;

          if (!baseAmount) {
            continue;
          }

          try {
            const result = await dispatch(convertCurrencyAmount({
              amount: baseAmount,
              fromCurrency: "EUR",  // Base currency
              toCurrency: targetCurrencyCode,  // Target currency code
              isShowLoader: false
            })).unwrap();

            if (result && result.convertedAmount !== undefined) {
              convertedResults[plan.id] = result.convertedAmount;
            }
          } catch (error) {
            // Fallback to local conversion if API fails
            try {
              const eurCurrency = getCurrencyByCode("EUR", currencies);
              const targetCurrency = getCurrencyByCode(targetCurrencyCode, currencies);
              
              if (eurCurrency && targetCurrency) {
                const localConversion = convertCurrency(baseAmount, eurCurrency.value, targetCurrency.value, currencies);
                convertedResults[plan.id] = localConversion.convertedAmount;
              } else {
                convertedResults[plan.id] = baseAmount;
              }
            } catch (localError) {
              convertedResults[plan.id] = baseAmount;
            }
          }
        }

        setConvertedAmounts(convertedResults);
      } catch (error) {
        setConvertedAmounts({});
      } finally {
        setIsConvertingAmounts(false);
      }
    };

    convertPlanAmounts();
  }, [selectedCountry, plans, currencies, dispatch]);

  // Initialize subscription options when data loads
  useEffect(() => {
    if (availableProvinces.length > 0 && subscriptionOptions.provinces.length === 0) {
      setSubscriptionOptions(prev => ({
        ...prev,
        provinces: [availableProvinces[0]]
      }));
    }
  }, [availableProvinces, subscriptionOptions.provinces.length]);

  useEffect(() => {
    if (availableCategories.length > 0 && subscriptionOptions.categories.length === 0) {
      setSubscriptionOptions(prev => ({
        ...prev,
        categories: [availableCategories[0]]
      }));
    }
  }, [availableCategories, subscriptionOptions.categories.length]);

  // No default country selection - user must select manually

  const addProvince = (province) => {
    if (!subscriptionOptions.provinces.includes(province)) {
      setSubscriptionOptions({
        ...subscriptionOptions,
        provinces: [...subscriptionOptions.provinces, province]
      });
    }
  };

  const removeProvince = (province) => {
    if (subscriptionOptions.provinces.length > 1) {
      setSubscriptionOptions({
        ...subscriptionOptions,
        provinces: subscriptionOptions.provinces.filter(p => p !== province)
      });
    }
  };

  const addCategory = (category) => {
    if (!subscriptionOptions.categories.includes(category)) {
      setSubscriptionOptions({
        ...subscriptionOptions,
        categories: [...subscriptionOptions.categories, category]
      });
    }
  };

  const removeCategory = (category) => {
    if (subscriptionOptions.categories.length > 1) {
      setSubscriptionOptions({
        ...subscriptionOptions,
        categories: subscriptionOptions.categories.filter(c => c !== category)
      });
    }
  };

  // Track when verification data has loaded
  useEffect(() => {
    if (verificationData !== null && verificationData !== undefined) {
      setVerificationDataLoaded(true);
    }
  }, [verificationData]);

  // Check if user needs to complete profile
  const checkProfileCompletion = () => {
    if (user && !user.phone) {
      setShowProfileCompletionDialog(true);
      return false; // Profile incomplete
    }
    return true; // Profile complete
  };

  // Check if user needs verification
  const checkVerification = () => {
    // Don't check verification if data hasn't loaded yet
    if (!verificationDataLoaded) {
      return true; // Allow action to proceed while loading
    }
    
    if (!isVerified()) {
      setShowVerificationDialog(true);
      return false; // Verification incomplete
    }
    return true; // Verification complete
  };

  // Check both profile and verification
  const checkRequirements = () => {
    // Don't block actions if verification data is still loading
    if (!verificationDataLoaded) {
      // Only check profile completion while verification loads
      if (!checkProfileCompletion()) {
        return false;
      }
      return true;
    }
    
    // Check verification first (higher priority)
    if (!checkVerification()) {
      return false;
    }
    
    // Then check profile completion
    if (!checkProfileCompletion()) {
      return false;
    }
    
    return true;
  };

  // Helper function to navigate to profile based on user role
  const navigateToProfile = () => {
    if (!user?.role?.id) return;
    
    const roleId = Number(user.role.id);
    if (roleId === RoleEnum.professional || roleId === RoleEnum.internationl_professional) {
      navigate("/myprofile");
    } else if (roleId === RoleEnum.client || roleId === RoleEnum.internationl_client) {
      navigate("/client/profile");
    } else if (roleId === RoleEnum.admin) {
      navigate("/admin/dashboard");
    }
    setShowProfileCompletionDialog(false);
  };

  // Handle billing saved callback
  const handleBillingSaved = () => {
    // Check if user now has billing information
    const billingCheck = checkUserBillingInfo(user);
    
    if (billingCheck.hasBillingInfo && selectedPlanForPayment) {
      // Find the area for the selected country (only if country is selected)
      const selectedArea = selectedCountry ? selectedPlanForPayment.areas?.find(area => area.country?.id === selectedCountry.id) : null;

      const isFallbackPricing = Boolean(selectedCountry && !selectedArea);
      const fallbackCurrencyCode = "EUR";
      const fallbackCurrencySymbol = getCurrencySymbol("EUR");
      const fallbackCurrencyId = getCurrencyIdByCode("EUR") || "EUR";
 
      // Use area-specific data only if country is selected AND area is found, otherwise use base plan data
      const planAmount = selectedCountry && selectedArea ? selectedArea.amount : selectedPlanForPayment.amount;

      // Set selected plan and open payment modal
      setSelectedPlan({
        ...selectedPlanForPayment,
        amount: planAmount,
        selectedArea,
        selectedCurrencyId: isFallbackPricing
          ? fallbackCurrencyId
          : selectedCountry?.currency?.id,
        selectedCurrencyCode: isFallbackPricing
          ? fallbackCurrencyCode
          : selectedCountry?.currency?.initial || selectedCountry?.currency?.code || "EUR",
        currencySymbol: isFallbackPricing
          ? fallbackCurrencySymbol
          : selectedCountry?.currency?.symbol || getCurrencySymbolForDisplay(),
        isFallbackPricing,
      });
      setShowPaymentModal(true);
      setSelectedPlanForPayment(null);
    }
  };

  // Handle plan selection
  const handlePlanSelect = (plan) => {
    // Check if user needs to complete profile and verification
    if (!checkRequirements()) {
      return;
    }

    // Check if user has billing information
    const billingCheck = checkUserBillingInfo(user);
    
    if (!billingCheck.hasBillingInfo) {
      // Store the plan for auto-opening payment modal after billing setup
      setSelectedPlanForPayment(plan);
      // Show billing address requirement dialog
      setShowBillingAddressDialog(true);
      return;
    }

    // Use the converted amount that was calculated in the component
    // This ensures consistency between what user saw and what they're paying
    const isFallbackPricing = Boolean(plan.isFallbackPricing);
    const fallbackCurrencyCode = "EUR";
    const fallbackCurrencySymbol = getCurrencySymbol("EUR");
    const fallbackCurrencyId = getCurrencyIdByCode("EUR") || "EUR";

    const planAmount = isFallbackPricing
      ? plan.baseAmount || plan.amount
      : plan.convertedAmount || plan.baseAmount || plan.amount;
 
    // Set selected plan and open payment modal
    setSelectedPlan({
      ...plan,
      amount: planAmount,
      baseAmount: plan.baseAmount || plan.amount,
      convertedAmount: isFallbackPricing ? undefined : plan.convertedAmount,
      selectedCurrencyCode: isFallbackPricing ? fallbackCurrencyCode : plan.selectedCurrencyCode || "EUR",
      selectedCurrencyId: isFallbackPricing
        ? fallbackCurrencyId
        : plan.selectedCurrencyId || selectedCountry?.currency?.id,
      currencySymbol: isFallbackPricing
        ? fallbackCurrencySymbol
        : plan.currencySymbol || getCurrencySymbolForDisplay(),
      isFallbackPricing,
      selectedArea: plan.selectedArea
    });
    setShowPaymentModal(true);
  };

  // Transform user credits to transaction format
  const transactions = userCredits.map(credit => {
    const isUsed = Number(credit.status?.id) === CreditStatusEnum.Used || Number(credit.status?.id) === CreditStatusEnum.Used_Emergency;
    const isCredit = credit.credit > 0;
    
    let description;
    if (isUsed) {
      description = t("creditsPage.creditsUsed");
    } else if (isCredit) {
      description = t("creditsPage.creditsPurchased");
    } else {
      description = t("creditsPage.creditPurchase", { name: credit.subscriptionPlan?.name || "Credits" });
    }
    
    return {
      type: "credit",
      description: description,
      amount: credit.credit,
      date: new Date(credit.createdAt).toLocaleString(),
      isEmergency: false,
      isSubscription: false,
      credit: credit
    };
  });

  return (
    <PaymentMethodsProvider>
      <div className="min-h-screen bg-gray-50 flex flex-col items-center w-full" style={{ textAlign: "left" }}>
        <SharedHeader4 />

        {/* Main content wrapper */}
        <div className="w-full max-w-6xl px-4 sm:px-6 lg:px-8 mx-auto pt-8 sm:pt-12 pb-16" style={{ textAlign: "left" }}>
          <div className="w-full">
            {/* Header Section */}
            <div className="mb-6 sm:mb-8 text-center">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">{t("creditsPage.title")}</h1>
              <p className="text-gray-600 text-sm sm:text-base">{t("creditsPage.subtitle")}</p>
              {selectedCountry ? (
                <div className="mt-3 flex justify-center">
                  <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-3 py-1">
                    <Globe className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">
                      {t("creditsPage.selectedCountry", { country: selectedCountry.name, symbol: getCurrencySymbolForDisplay() })}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex justify-center">
                  <div className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-3 py-1">
                    <Globe className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-600">
                      {t("creditsPage.selectCountryPrompt")}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Country Selector */}
            <div className="mb-5 sm:mb-6 flex justify-center sm:justify-end">
              <div className="w-full sm:w-80">
                <CountryDropdown
                  selectedCountry={selectedCountry}
                  onCountrySelect={async (country: any) => {
                    if (country) {
                      const normalizeString = (value?: string | null) => {
                        if (!value) return "";
                        return value
                          .normalize("NFD")
                          .replace(/[\u0300-\u036f]/g, "")
                          .toLowerCase();
                      };

                      const mapCurrencyResult = (currencyData: any) => {
                        if (!currencyData) {
                          return null;
                        }
                        const initialCode =
                          currencyData.initial ||
                          currencyData.code ||
                          currencyData.label ||
                          "";
                        const resolvedSymbol =
                          currencyData.symbol ||
                          (initialCode ? getCurrencySymbol(initialCode) : undefined);
                        return {
                          id: currencyData.id || currencyData.value || "",
                          initial: initialCode,
                          code: initialCode,
                          name: currencyData.name || currencyData.label || "",
                          symbol: resolvedSymbol,
                          exchangeRate: currencyData.exchangeRate,
                        };
                      };

                      const matchCurrency = async (): Promise<any | null> => {
                        const countryCode = country.code?.toUpperCase();
                        const countryInitial = country.initial?.toUpperCase();

                        if (
                          (countryCode && EURO_CURRENCY_COUNTRIES.has(countryCode)) ||
                          (countryInitial && EURO_CURRENCY_COUNTRIES.has(countryInitial))
                        ) {
                          try {
                            const eurCurrency = await getCurrencyByCodeAction("EUR");
                            if (eurCurrency) {
                              return mapCurrencyResult({
                                id: eurCurrency.id,
                                initial: eurCurrency.initial,
                                code: eurCurrency.initial,
                                name: eurCurrency.name,
                                symbol: eurCurrency.symbol,
                                exchangeRate: eurCurrency.exchangeRate,
                              });
                            }
                          } catch (_error) {
                            // Ignore and fall through
                          }
                        }

                        // Fetch from backend only
                        const searchTerms = [
                          country.initial,
                          country.code,
                          country.name,
                          normalizeString(country.name || ""),
                        ].filter(Boolean) as string[];

                        for (const term of searchTerms) {
                          const upperTerm = term.toUpperCase();

                          // Try exact code match via backend endpoint
                          if (upperTerm.length <= 5) {
                            try {
                              const currencyByCode = await getCurrencyByCodeAction(upperTerm);
                              if (currencyByCode) {
                                return mapCurrencyResult({
                                  id: currencyByCode.id,
                                  initial: currencyByCode.initial,
                                  code: currencyByCode.initial,
                                  name: currencyByCode.name,
                                  symbol: currencyByCode.symbol,
                                  exchangeRate: currencyByCode.exchangeRate,
                                });
                              }
                            } catch (_error) {
                              // Ignore and continue to fallback search
                            }
                          }

                          // Fallback to name-based search via backend pagination endpoint
                          try {
                            const response = await getCurrencies({
                              name: term,
                              limit: 10,
                              page: 1,
                            });

                            const dataset = Array.isArray(response?.data)
                              ? response.data
                              : Array.isArray(response)
                              ? response
                              : [];

                            const remoteMatch = dataset.find((item: any) => {
                              if (!item) return false;
                              const itemInitial = item.initial?.toUpperCase();
                              const itemName = normalizeString(item.name || "");
                              const targetName = normalizeString(term);
                              return (
                                itemInitial === upperTerm ||
                                (targetName && itemName.includes(targetName))
                              );
                            });

                            if (remoteMatch) {
                              return mapCurrencyResult({
                                id: remoteMatch.id,
                                initial: remoteMatch.initial,
                                code: remoteMatch.initial,
                                name: remoteMatch.name,
                                symbol: remoteMatch.symbol,
                                exchangeRate: remoteMatch.exchangeRate,
                              });
                            }
                          } catch (_error) {
                            // Ignore backend fetch errors and continue to next term
                          }
                        }

                        try {
                          const eurCurrency = await getCurrencyByCodeAction("EUR");
                          if (eurCurrency) {
                            return mapCurrencyResult({
                              id: eurCurrency.id,
                              initial: eurCurrency.initial,
                              code: eurCurrency.initial,
                              name: eurCurrency.name,
                              symbol: eurCurrency.symbol,
                              exchangeRate: eurCurrency.exchangeRate,
                            });
                          }
                        } catch (_error) {
                          // Ignore and fall through
                        }

                        return {
                          id: "EUR",
                          initial: "EUR",
                          code: "EUR",
                          name: "Euro",
                          symbol: "€",
                          exchangeRate: 1,
                        };
                      };

                      const matchedCurrency = await matchCurrency();

                      if (matchedCurrency?.id) {
                        const codeForCache = matchedCurrency.initial || matchedCurrency.code;
                        const symbolForCache =
                          matchedCurrency.symbol ||
                          (codeForCache ? getCurrencySymbol(codeForCache) : undefined);

                        if (codeForCache) {
                          setCachedCurrencyPreference(matchedCurrency.id, {
                            currencyCode: codeForCache,
                            currencySymbol: symbolForCache,
                          });
                        }
                      }

                      const countryWithCurrency = {
                        ...country,
                        currency: matchedCurrency
                          ? {
                              id: matchedCurrency.id,
                              initial: matchedCurrency.initial,
                              code: matchedCurrency.code,
                              name: matchedCurrency.name,
                              symbol: matchedCurrency.symbol,
                              exchangeRate: matchedCurrency.exchangeRate,
                            }
                          : null,
                      };

                      setSelectedCountry(countryWithCurrency);
                      setCachedCountryPreference(country.id);
                    } else {
                      setSelectedCountry(null);
                    }
                  }}
                  placeholder={t("creditsPage.selectCountry")}
                  autoDetect={true}
                  disabled={isConvertingAmounts}
                />
              </div>
            </div>

            {/* Credit Balance & Quick Actions */}
            <div className="grid gap-4 sm:gap-5 md:gap-6 grid-cols-1 md:grid-cols-3 mb-6 sm:mb-8">
              <Card className="overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 text-white h-full">
                <div className="p-4 sm:p-5 md:p-6">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div className="p-2 bg-white/10 rounded-lg">
                      <CreditCard className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <Button variant="ghost" className="text-white hover:bg-white/10 text-xs sm:text-sm py-1 px-2.5 h-auto">
                      {t("creditsPage.viewDetails")}
                    </Button>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold mb-1">
                    {userBalanceLoading ? (
                      <Loader2 className="h-6 w-6 animate-spin" />
                    ) : (
                      `${userBalance?.availableCredits || 0}`
                    )}
                  </h2>
                  <p className="text-blue-100 text-sm">{t("creditsPage.availableCreditBalance")}</p>
                </div>
              </Card>

              <Card className="overflow-hidden bg-gradient-to-br from-purple-500 to-purple-600 text-white h-full">
                <div className="p-4 sm:p-5 md:p-6">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div className="p-2 bg-white/10 rounded-lg">
                      <Clock className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <Button variant="ghost" className="text-white hover:bg-white/10 text-xs sm:text-sm py-1 px-2.5 h-auto">
                      {t("creditsPage.viewDetails")}
                    </Button>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold mb-1">{userBalance?.emergencyAvailableCredits || 0}</h2>
                  <p className="text-purple-100 text-sm">{t("creditsPage.availableEmergencyBalance")}</p>
                </div>
              </Card>

              {/* <Card className="overflow-hidden h-full">
                <div className="p-4 sm:p-5 md:p-6 h-full flex flex-col">
                  <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 text-sm sm:text-base">Subscription Status</h3>
                      <p className="text-xs sm:text-sm text-gray-500">Active until Mar 31, 2025</p>
                    </div>
                  </div>
                  <Button variant="outline" className="w-full mt-auto text-xs sm:text-sm">
                    Manage Subscriptions
                  </Button>
                </div>
              </Card> */}
            </div>

            {/* Credit Type Tabs */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-5 sm:mb-6 gap-3 sm:gap-0">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">{t("creditsPage.purchaseOptions")}</h2>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button
                  variant={creditType === "regular" ? "default" : "outline"}
                  onClick={() => setCreditType("regular")}
                  className="flex-1 sm:flex-none text-xs sm:text-sm py-2 px-3 sm:py-2 sm:px-4 h-auto"
                >
                  {t("creditsPage.regularCredits")}
                </Button>
                <Button
                  variant={creditType === "emergency" ? "default" : "outline"}
                  className={`flex-1 sm:flex-none text-xs sm:text-sm py-2 px-3 sm:py-2 sm:px-4 h-auto ${creditType === "emergency" ? "bg-purple-600 hover:bg-purple-700" : ""}`}
                  onClick={() => setCreditType("emergency")}
                >
                  <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  {t("creditsPage.emergencySubscription")}
                </Button>
              </div>
            </div>

            {/* Regular Credits Section */}
            {creditType === "regular" && (
              <div className="mb-8 sm:mb-10 md:mb-12">
                <div className="bg-blue-50 rounded-lg p-3 sm:p-4 mb-5 sm:mb-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-full flex-shrink-0">
                      <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-blue-800 mb-1 text-sm sm:text-base">{t("creditsPage.regularCreditPlans")}</h3>
                      <p className="text-xs sm:text-sm text-blue-600">
                        {t("creditsPage.regularCreditPlansDesc")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Loading State */}
                {plansLoading && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-600">{t("creditsPage.loadingCreditPlans")}</span>
                  </div>
                )}

                {/* Error State */}
                {plansError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="h-5 w-5" />
                      <div>
                        <p className="font-medium">{t("creditsPage.errorLoadingPlans")}</p>
                        <p className="text-sm">{plansError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Plans Grid */}
                {!plansLoading && displayPlans.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {isConvertingAmounts ? (
                      <div className="col-span-full flex items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                        <span className="ml-2 text-gray-600">{t("creditsPage.convertingAmounts")}</span>
                      </div>
                    ) : (
                      displayPlans.map((plan) => (
                        <CreditPackage
                          key={plan.id}
                          plan={plan}
                          onSelect={handlePlanSelect}
                          currencySymbol={getCurrencySymbolForDisplay()}
                          selectedCountry={selectedCountry}
                          currencies={currencies}
                          selectedCurrencyCode={selectedCurrencyCode}
                          convertedAmounts={convertedAmounts}
                        />
                      ))
                    )}
                  </div>
                )}

                {/* No Plans State */}
                {!plansLoading && displayPlans.length === 0 && !plansError && (
                  <div className="text-center py-8">
                    <Wallet className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {t("creditsPage.noRegularPlansAvailable")}
                    </h3>
                    <p className="text-gray-500">
                      {t("creditsPage.checkBackLater")}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Emergency Subscription Section */}
            {creditType === "emergency" && (
              <div className="mb-8 sm:mb-10 md:mb-12">
                <div className="bg-red-50 rounded-lg p-3 sm:p-4 mb-5 sm:mb-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-red-100 rounded-full flex-shrink-0">
                      <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-red-800 mb-1 text-sm sm:text-base">{t("creditsPage.emergencyServiceSubscriptions")}</h3>
                      <p className="text-xs sm:text-sm text-red-600">
                        {t("creditsPage.emergencySubscriptionsDesc")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Loading State */}
                {plansLoading && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-red-600" />
                    <span className="ml-2 text-gray-600">{t("creditsPage.loadingEmergencyPlans")}</span>
                  </div>
                )}

                {/* Error State */}
                {plansError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="h-5 w-5" />
                      <div>
                        <p className="font-medium">{t("creditsPage.errorLoadingEmergencyPlans")}</p>
                        <p className="text-sm">{plansError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Plans Grid */}
                {!plansLoading && displayPlans.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {isConvertingAmounts ? (
                      <div className="col-span-full flex items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
                        <span className="ml-2 text-gray-600">{t("creditsPage.convertingAmounts")}</span>
                      </div>
                    ) : (
                      displayPlans.map((plan) => (
                        <EmergencyPlanPackage
                          key={plan.id}
                          plan={plan}
                          onSelect={handlePlanSelect}
                          currencySymbol={getCurrencySymbolForDisplay()}
                          selectedCountry={selectedCountry}
                          currencies={currencies}
                          selectedCurrencyCode={selectedCurrencyCode}
                          convertedAmounts={convertedAmounts}
                        />
                      ))
                    )}
                  </div>
                )}

                {/* No Plans State */}
                {!plansLoading && displayPlans.length === 0 && !plansError && (
                  <div className="text-center py-8">
                    <Clock className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {t("creditsPage.noEmergencyPlansAvailable")}
                    </h3>
                    <p className="text-gray-500">
                      {t("creditsPage.checkBackLaterEmergency")}
                    </p>
                  </div>
                )}

                {/* Information Card */}
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-1 bg-amber-100 rounded-full flex-shrink-0">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-amber-800 text-xs sm:text-sm mb-1">{t("creditsPage.importantInformation")}</h3>
                      <p className="text-xs sm:text-sm text-amber-700">
                        {t("creditsPage.emergencySubscriptionInfo")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Transaction History */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="p-4 sm:p-5 md:p-6 border-b">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">{t("creditsPage.transactionHistory")}</h2>
                  <Button variant="outline" className="gap-2 text-xs sm:text-sm py-1.5 px-3 h-auto">
                    <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    {t("creditsPage.export")}
                  </Button>
                </div>

                {/* Filters */}
                <div className="mt-4 sm:mt-5 grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4">
                  <div className="md:col-span-6">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder={t("creditsPage.searchTransactions")}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-1.5 sm:py-2 border rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-1 md:col-span-3 gap-2 sm:gap-3">
                    <select
                      className="border rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={selectedPeriod}
                      onChange={(e) => setSelectedPeriod(e.target.value)}
                    >
                      <option value="all">{t("creditsPage.allTime")}</option>
                      <option value="month">{t("creditsPage.thisMonth")}</option>
                      <option value="week">{t("creditsPage.thisWeek")}</option>
                    </select>
                  </div>

                  <div className="md:col-span-3">
                    <select
                      className="w-full border rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                    >
                      <option value="all">{t("creditsPage.allTypes")}</option>
                      <option value="credits">{t("creditsPage.credits")}</option>
                      <option value="subscription">{t("creditsPage.subscriptions")}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                {userCreditsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-600">{t("creditsPage.loadingTransactionHistory")}</span>
                  </div>
                ) : transactions.length > 0 ? (
                  transactions.map((transaction, index) => (
                    <TransactionRow key={index} transaction={transaction} />
                  ))
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {t("creditsPage.noCreditTransactions")}
                    </h3>
                    <p className="text-gray-500">
                      {t("creditsPage.noCreditTransactionsDesc")}
                    </p>
                  </div>
                )}
              </div>

              <div className="p-4 sm:p-5 md:p-6 border-t">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                  <p className="text-xs sm:text-sm text-gray-500">
                    {t("creditsPage.showingTransactions", { count: transactions.length, total: transactions.length })}
                  </p>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Button variant="outline" disabled className="flex-1 sm:flex-none text-xs sm:text-sm py-1.5 px-3 h-auto">
                      {t("creditsPage.previous")}
                    </Button>
                    <Button variant="outline" className="flex-1 sm:flex-none text-xs sm:text-sm py-1.5 px-3 h-auto">
                      {t("creditsPage.next")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <br></br>
        <br></br>
        <br></br>

        <SharedFooter3 />

        {/* CreditPaymentModal */}
        {selectedPlan && (
          <CreditPaymentModal
            isOpen={showPaymentModal}
            onClose={() => {
              setShowPaymentModal(false);
              setSelectedPlan(null);
            }}
            onSuccess={(paymentData) => {
              setShowPaymentModal(false);
              setSelectedPlan(null);
              // Refresh user credits and balance after successful payment
              if (user?.id) {
                dispatch(fetchUserCredits({ userId: user.id }));
                dispatch(fetchUserCreditBalance(user.id));
              }
            }}
            planAmount={selectedPlan.amount}
            planName={selectedPlan.name}
            currencySymbol={selectedPlan.currencySymbol || getCurrencySymbol(selectedPlan.selectedCurrencyCode || "EUR")}
            selectedPlan={selectedPlan}
            selectedCurrencyCode={selectedPlan.selectedCurrencyCode}
            baseAmount={selectedPlan.baseAmount}
          />
        )}

        {/* Profile Completion Dialog */}
        <ProfileCompletionDialog
          isOpen={showProfileCompletionDialog}
          onClose={() => setShowProfileCompletionDialog(false)}
          onGoToProfile={navigateToProfile}
          userRole={Number(user?.role?.id)}
        />

        {/* Verification Requirement Dialog */}
        <VerificationRequirementDialog
          isOpen={showVerificationDialog}
          onClose={() => setShowVerificationDialog(false)}
          title="Verification Required"
          message="Please complete your account verification to purchase credits or subscriptions."
        />

        {/* Billing Address Requirement Dialog */}
        <BillingAddressRequirementDialog
          isOpen={showBillingAddressDialog}
          onClose={() => {
            setShowBillingAddressDialog(false);
            setSelectedPlanForPayment(null);
          }}
          onBillingSaved={handleBillingSaved}
          userRole={Number(user?.role?.id)}
          action="payment"
        />
      </div>
    </PaymentMethodsProvider>
  );
};

export default CreditsPage;